import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useMemo, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { stepTestNamesFromFiles } from "../../shared/stepTests/constants";
import { useNavigateOnBranchChange } from "../hooks/useNavigateOnBranchChange";
import { useStepTestExecutions } from "../hooks/useStepExecutions";
import { useQuery } from "../hooks/useQuery";
import { useStepTestRows } from "../hooks/useStepTestRows";
import { Button } from "./Button";
import { EmptyState } from "./EmptyState";
import { SoftButton } from "./SoftButton";
import { StepTestStatusDot } from "./StepTestStatusDot";
import { StepTestUnpinButton } from "./StepTestUnpinButton";
import { StepTestUnpinConfirmModal } from "./StepTestUnpinConfirmModal";
import { Text } from "./Text";
import type { StepData } from "./Workflow";

const Wrap = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  overflow-y: auto;
`;

const Row = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SpecList = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Message = styled("div")`
  padding-left: 16px;
  color: var(--text-secondary);
  white-space: pre-wrap;
  overflow-wrap: break-word;
`;

type Props = {
  step: StepData;
  workflowId: string;
  branchId: string;
  commitId: string;
  files: readonly { path: string }[];
  isMainBranch: boolean;
  onInvestigateFailure: (params: { stepDir: string; testName: string }) => void;
};

export function StepTests({
  step,
  workflowId,
  branchId,
  commitId,
  files,
  isMainBranch,
  onInvestigateFailure,
}: Props) {
  const z = useZero();
  const navigateOnBranchChange = useNavigateOnBranchChange(branchId);
  // `files` paths are already step-relative — selectStepFiles strips the step
  // prefix upstream — so match the step-test files directly, without re-stripping.
  const stepTestNames = useMemo(() => stepTestNamesFromFiles(files), [files]);

  const [commitFiles] = useQuery(queries.loadAllCommitFiles({ commitId }));
  const { executions } = useStepTestExecutions(workflowId, branchId, step.id);

  const { rows, couldNotRun } = useStepTestRows({
    commitFiles: commitFiles ?? [],
    stepDir: step.id,
    testNames: stepTestNames,
    executions,
  });

  const [unpinName, setUnpinName] = useState<string | null>(null);
  const handleUnpin = (name: string) => {
    void navigateOnBranchChange((branchParams) =>
      z.mutate(
        sharedMutators.unpinStepTest({
          workflowId,
          ...branchParams,
          stepDir: step.id,
          name,
        })
      )
    );
  };

  if (stepTestNames.length === 0) {
    return (
      <EmptyState
        iconName="list-todo"
        iconSize={24}
        maxWidth={260}
        title="No step tests yet"
        description="Pin a successful run as a step test to re-check this step after edits."
      />
    );
  }

  return (
    <Wrap>
      <Row>
        <Text variant="text-small-md">
          {stepTestNames.length} step test
          {stepTestNames.length === 1 ? "" : "s"}
        </Text>
        <Button
          variant="secondary"
          onClick={() => {
            z.mutate(
              sharedMutators.runStepTests({
                runId: nanoid(12),
                workflowId,
                commitId,
                stepDir: step.id,
                executionId: nanoid(12),
              })
            );
          }}
        >
          Check step tests
        </Button>
      </Row>
      <SpecList>
        {rows.map((r) => (
          <div key={r.name}>
            <Row>
              <StepTestStatusDot status={r.status} />
              <Text variant="text-small">{r.name}</Text>
              {r.status === "failed" && (
                <SoftButton
                  variant="secondary"
                  onClick={() =>
                    onInvestigateFailure({
                      stepDir: step.id,
                      testName: r.name,
                    })
                  }
                >
                  Investigate failure
                </SoftButton>
              )}
              {!isMainBranch && (
                <StepTestUnpinButton
                  name={r.name}
                  onPress={() => setUnpinName(r.name)}
                />
              )}
            </Row>
            {r.message && (
              <Message>
                <Text variant="text-small" color="var(--text-secondary)">
                  {r.message}
                </Text>
              </Message>
            )}
          </div>
        ))}
        {couldNotRun && (
          <Text variant="text-small" color="var(--red-600)">
            Step tests could not run.
          </Text>
        )}
      </SpecList>
      <StepTestUnpinConfirmModal
        name={unpinName}
        onClose={() => setUnpinName(null)}
        onConfirm={handleUnpin}
      />
    </Wrap>
  );
}
